import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ArrowLeft, MailCheck } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-background relative z-10">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md"
      >
        <div className="flex justify-center mb-6">
          <img src="/logo.png" alt="Travel Mor" className="w-24 h-24 object-contain" />
        </div>

        {sent ? (
          <div className="text-center glass rounded-lg p-8" data-testid="forgot-success">
            <MailCheck className="w-10 h-10 text-primary mx-auto mb-4" />
            <h2 className="font-serif text-3xl font-light tracking-tighter mb-2">Check your inbox</h2>
            <p className="text-muted-foreground text-sm mb-8">
              If that email is registered, a reset link has been sent. It expires in one hour.
            </p>
            <Link to="/login" className="text-primary text-sm inline-flex items-center gap-2 hover:opacity-80">
              <ArrowLeft className="w-4 h-4" /> Back to sign in
            </Link>
          </div>
        ) : (
          <>
            <h2 className="font-serif text-4xl font-light tracking-tighter mb-2 text-center">Reset password</h2>
            <p className="text-muted-foreground text-sm mb-10 text-center">
              Enter your email and we'll send you a reset link.
            </p>
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="forgot-form">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-xs tracking-widest uppercase text-muted-foreground">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="forgot-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="bg-input border-border h-12 focus-visible:ring-primary"
                />
              </div>
              {error && <p className="text-sm text-destructive-foreground">{error}</p>}
              <Button
                type="submit"
                data-testid="forgot-submit-btn"
                disabled={loading}
                className="w-full h-12 rounded-full bg-primary text-primary-foreground font-medium hover:scale-[1.02] transition-transform gold-glow"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send reset link"}
              </Button>
              <Link to="/login" className="text-muted-foreground text-sm flex items-center justify-center gap-2 hover:text-foreground">
                <ArrowLeft className="w-4 h-4" /> Back to sign in
              </Link>
            </form>
          </>
        )}
      </motion.div>
    </div>
  );
}
